const boostTypes = (listing = {}) => {
  const values = [
    ...(Array.isArray(listing.activeBoosts) ? listing.activeBoosts : []),
    ...(Array.isArray(listing.activePromotionTypes) ? listing.activePromotionTypes : []),
    listing.dopingType,
    listing.promotionType
  ]
  return new Set(values.filter(Boolean).map(value => String(value).toLowerCase()))
}

const hasAny = (types, candidates) => candidates.some(type => types.has(type))

const isBumpActive = (listing = {}, types = boostTypes(listing)) => {
  if (listing.activePromotionBadges && typeof listing.activePromotionBadges.bumped === 'boolean') {
    return listing.activePromotionBadges.bumped
  }
  if (listing.promotionBadge === 'bumped') return true
  if (listing.promotionBumpedUntil) return new Date(listing.promotionBumpedUntil) > new Date()
  return hasAny(types, ['listing_bump_manual', 'listing_bump_auto', 'bumped'])
}

const discountPercent = (listing = {}) => {
  const value = Number(
    listing.promotionDiscount?.percent ??
    listing.weeklyDealDiscountPercent ??
    listing.discountPercent ??
    listing.discount
  )
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0
}

/**
 * Buyer-facing promotion contract. Internal package keys never become labels.
 * Promotion badges are separate from stock, delivery and "Yeni" card metadata.
 */
export const getListingPromotionBadges = (listing = {}, { limit = 2, source = '' } = {}) => {
  const types = boostTypes(listing)
  const percent = discountPercent(listing)
  const badges = []

  // Category-detail ranking has one explicit disclosure per tier. Other
  // placements (bazar/search/weekly/banner) must not explain this ordering.
  if (source === 'category_detail') {
    if (listing.promotionTier === 'home_showcase') return [{ key: 'showcase', label: '💎 Vitrin', tone: 'showcase' }]
    if (listing.promotionTier === 'listing_highlight') return [{ key: 'highlight', label: '⚡ Önə çıxarılıb', tone: 'highlight' }]
    if (listing.promotionTier === 'normal') return []
  }

  const weekly = listing.isWeeklyDeal || listing.promotionWeeklyDeal?.enabled || hasAny(types, ['weekly_deal', 'heftenin_fursetleri'])
  const homeShowcase = hasAny(types, ['home_showcase', 'home_featured', 'anasayfa_vitrini', 'ana_sehife_vitrini'])
  const marketplaceShowcase = hasAny(types, ['marketplace_showcase', 'marketplace_featured', 'bazar_vitrini'])
  const searchShowcase = hasAny(types, ['search_showcase', 'search_featured', 'arama_vitrini', 'axtaris_vitrini'])
  const highlighted = hasAny(types, ['featured_listing', 'listing_highlight']) || listing.isHighlighted === true
  const hasDiscount = percent > 0 || listing.promotionDiscount?.enabled

  // Search placement is the reason this result ranks first, so keep its small
  // disclosure visible before applying the shared two-badge limit.
  if (source === 'search_results' && listing.searchShowcasePromoted === true && searchShowcase) {
    badges.push({ key: 'search', label: 'Sponsor', tone: 'search' })
  }
  if (weekly) badges.push({ key: 'weekly', label: '🔥 Həftənin fürsəti', tone: 'weekly' })
  // Weekly price context is more useful to a buyer than a second placement label.
  if (weekly && hasDiscount) badges.push({ key: 'discount', label: percent > 0 ? `-${percent}%` : 'Endirim', tone: 'discount' })
  // Placement badges are surface-specific. A product may own more than one
  // effect, but each badge/frame is shown only in its purchased placement.
  if ((!source || source === 'home_showcase' || source === 'category_detail') && homeShowcase) badges.push({ key: 'showcase', label: '💎 Vitrin', tone: 'showcase' })
  if ((!source || source === 'marketplace_showcase') && marketplaceShowcase) badges.push({ key: 'marketplace', label: '🚀 Bazar vitrini', tone: 'marketplace' })
  if (!weekly && hasDiscount) {
    badges.push({ key: 'discount', label: percent > 0 ? `-${percent}%` : 'Endirim', tone: 'discount' })
  }
  if (highlighted) badges.push({ key: 'highlight', label: '⚡ Önə çıxarılıb', tone: 'highlight' })

  // A bump is normal-feed recency, not a showcase. Keep it subtle and only when
  // no higher-priority promotion already explains the card's placement.
  if (!badges.length && isBumpActive(listing, types)) {
    badges.push({ key: 'bumped', label: 'Yuxarıda', tone: 'bumped' })
  }

  return badges.slice(0, Math.max(0, limit))
}

// Backward-compatible single-badge API for older card implementations.
export const getPromotionBadge = (listing = {}) => getListingPromotionBadges(listing, { limit: 1 })[0] || null

export const getPromotionFrameClass = (listing = {}, options = {}) => {
  const badges = getListingPromotionBadges(listing, options)
  if (badges.some(badge => badge.key === 'showcase')) return 'promotion-frame promotion-frame--home'
  if (badges.some(badge => badge.key === 'marketplace')) return 'promotion-frame promotion-frame--marketplace'
  if (badges.some(badge => badge.key === 'highlight')) return 'promotion-frame promotion-frame--highlight'
  return 'border-[#E9DDFB]'
}
